import React from 'react';
import { Chip } from '@heroui/react';
import type { Lead } from '@/types';

interface LeadScoreProps {
  lead: Lead;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * LeadScore component - Displays lead score with color coding based on competitor_count and score
 * Color logic:
 * - 1 competitor or score <= 15% → red (danger)
 * - 2 competitors or score 16-30% → orange (warning)
 * - 3+ competitors or score > 30% → green (success)
 */
export function LeadScore({ lead, showLabel = false, size = 'sm' }: LeadScoreProps) {
  if (lead.score === null) {
    return <span className="text-default-400 text-sm">-</span>;
  }

  // Color logic: check competitor_count first, then score
  const scorePercent = lead.score * 100;
  let scoreColor: 'success' | 'warning' | 'danger' = 'danger';

  if (lead.competitor_count >= 3 || scorePercent > 30) {
    scoreColor = 'success';
  } else if (lead.competitor_count === 2 || (scorePercent > 15 && scorePercent <= 30)) {
    scoreColor = 'warning';
  } else if (lead.competitor_count === 1 || scorePercent <= 15) {
    scoreColor = 'danger';
  }

  const displayText = showLabel ? `Score: ${scorePercent.toFixed(0)}%` : `${scorePercent.toFixed(0)}%`;

  return (
    <Chip color={scoreColor} size={size} variant="flat">
      {displayText}
    </Chip>
  );
}
